import { Component, Inject, Injector, ViewChild, Directive, ViewContainerRef, ComponentRef, AfterViewInit, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SurveyModel, SurveyStep, SurveyStepType, SurveyResponse, SurveyOptionAction } from '@nx-c4g/c4g-models';
import { MultipleChoiceSingleSelectSurveyStepComponent } from './multiple-choice-single-select-survey-step/multiple-choice-single-select-survey-step.component';
import { MultipleChoiceSingleSelectWithTextSurveyStepComponent } from './multiple-choice-single-select-with-text-survey-step/multiple-choice-single-select-with-text-survey-step.component';
import { MultipleChoiceMultiSelectSurveyStepComponent } from './multiple-choice-multi-select-survey-step/multiple-choice-multi-select-survey-step.component';
import { MultipleChoiceMultiSelectWithTextSurveyStepComponent } from './multiple-choice-multi-select-with-text-survey-step/multiple-choice-multi-select-with-text-survey-step.component';
import { TextInputSurveyStepComponent } from './text-input-survey-step/text-input-survey-step.component';
import { YesNoSurveyStepComponent } from './yes-no-survey-step/yes-no-survey-step.component';
import { SliderSurveyStepComponent } from './slider-survey-step/slider-survey-step.component';
import { ToggleSwitchSurveyStepComponent } from './toggle-switch-survey-step/toggle-switch-survey-step.component';
import { RatingSurveyStepComponent } from './rating-survey-step/rating-survey-step.component';
import { InformationSurveyStepComponent } from './information-survey-step/information-survey-step.component';
import { IntroductionSurveyStepComponent } from './introduction-survey-step/introduction-survey-step.component';
import { SuccessScreenSurveyStepComponent } from './success-screen-survey-step/success-screen-survey-step.component';
import { AddressSurveyStepComponent } from './address-survey-step/address-survey-step.component';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { NgComponentOutlet } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'c4g-survey-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatProgressBarModule,
    MatIconModule,
    TranslateModule
],
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss']
})
export class SurveyDialogComponent implements AfterViewInit {
  surveyModel: SurveyModel;
  stepIdToIndex: { [stepId: string]: number } = {};
  currentStepId: string | null = null;
  responses: { [stepId: string]: SurveyResponse } = {};
  private isNavigating = false; // Prevent circular navigation
  editMode = false;

  @ViewChild('stepHost', { read: ViewContainerRef, static: false }) stepHost!: ViewContainerRef;
  private stepComponentRef: ComponentRef<any> | null = null;
  private viewInitialized = false;

  isStepsMenuOpen = false;
  menuPosition = { top: '0', left: '0' };

  @ViewChild('menuTrigger', { static: false }) menuTrigger!: ElementRef;
  @ViewChild('stepsMenu', { static: false }) stepsMenu!: ElementRef;
  @ViewChild('dialogContent', { static: true }) dialogContent!: ElementRef;

  // Mapping from SurveyStepType to component class
  stepTypeComponentMap: Record<SurveyStepType, any> = {
    [SurveyStepType.multipleChoiceSingleSelect]: MultipleChoiceSingleSelectSurveyStepComponent,
    [SurveyStepType.multipleChoiceSingleSelectWithText]: MultipleChoiceSingleSelectWithTextSurveyStepComponent,
    [SurveyStepType.multipleChoiceMultiSelect]: MultipleChoiceMultiSelectSurveyStepComponent,
    [SurveyStepType.multipleChoiceMultiSelectWithText]: MultipleChoiceMultiSelectWithTextSurveyStepComponent,
    [SurveyStepType.textInput]: TextInputSurveyStepComponent,
    [SurveyStepType.yesNo]: YesNoSurveyStepComponent,
    [SurveyStepType.slider]: SliderSurveyStepComponent,
    [SurveyStepType.toggleSwitch]: ToggleSwitchSurveyStepComponent,
    [SurveyStepType.rating]: RatingSurveyStepComponent,
    [SurveyStepType.information]: InformationSurveyStepComponent,
    [SurveyStepType.introduction]: IntroductionSurveyStepComponent,
    [SurveyStepType.successScreen]: SuccessScreenSurveyStepComponent,
    [SurveyStepType.address]: AddressSurveyStepComponent,
  };

  // Mapping of SurveyStepType to estimated seconds per step
  private stepTypeSeconds: Record<SurveyStepType, number> = {
    [SurveyStepType.multipleChoiceSingleSelect]: 20,
    [SurveyStepType.multipleChoiceSingleSelectWithText]: 40,
    [SurveyStepType.multipleChoiceMultiSelect]: 30,
    [SurveyStepType.multipleChoiceMultiSelectWithText]: 50,
    [SurveyStepType.textInput]: 40,
    [SurveyStepType.yesNo]: 15,
    [SurveyStepType.slider]: 15,
    [SurveyStepType.toggleSwitch]: 15,
    [SurveyStepType.rating]: 15,
    [SurveyStepType.information]: 10,
    [SurveyStepType.introduction]: 10,
    [SurveyStepType.successScreen]: 0,
    [SurveyStepType.address]: 45,
  };

  constructor(
    public dialogRef: MatDialogRef<SurveyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      survey: SurveyModel;
      surveyResponses?: SurveyResponse[];
    },
    private translate: TranslateService,
    private elementRef: ElementRef
  ) {
    this.surveyModel = data.survey;
  }

  ngOnInit() {
    this.surveyModel.steps.forEach((step, idx) => {
      this.stepIdToIndex[step._id] = idx;
    });

    // Initialize responses from previous survey responses if they exist
    if (this.data.surveyResponses?.length) {
      this.data.surveyResponses.forEach(response => {
        if (response.stepId) {
          this.responses[response.stepId] = response;
        }
      });
      this.editMode = true;
    }

    this.currentStepId = this.surveyModel.steps[0]?._id || null;
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.loadCurrentStepComponent();
  }

  get currentStep(): SurveyStep | null {
    if (!this.currentStepId) return null;
    const idx = this.stepIdToIndex[this.currentStepId];
    return this.surveyModel.steps[idx];
  }

  get currentStepComponent(): any | null {
    const step = this.currentStep;
    if (!step) return null;
    return this.stepTypeComponentMap[step.stepType] || null;
  }

  private loadCurrentStepComponent() {
    if (!this.stepHost) {
      return;
    }
    this.stepHost.clear();
    const componentType = this.currentStepComponent;
    if (!componentType) {
      return;
    }
    const injector = this.dynamicComponentInjector;
    if (!injector) {
      return;
    }
    this.stepComponentRef = this.stepHost.createComponent(componentType, {
      injector
    });
  }

  onStepResponse(response: SurveyResponse) {
    this.responses[response.stepId] = response;

    // For now, we only store the response and let the UI handle navigation
    // Conditional navigation will be triggered when Next button is clicked
  }

  private async handleResponseNavigation(response: SurveyResponse) {
    if (!this.isValidResponse(response)) {
      return;
    }

    const selectedOptions = this.getSelectedOptions(response);
    if (selectedOptions.length === 0) {
      return;
    }

    const actionToExecute = this.getPriorityAction(selectedOptions);
    if (actionToExecute) {
      await this.executeAction(actionToExecute);
    }
  }

  private isValidResponse(response: SurveyResponse): boolean {
    return !!(response?.response && response.response.length > 0 && this.currentStep);
  }

  private getSelectedOptions(response: SurveyResponse): any[] {
    const currentStep = this.currentStep;
    if (!currentStep) return [];

    return response.response
      .map(selectedResponse => currentStep.options.find(option => option._id === selectedResponse._id))
      .filter(option => option !== undefined);
  }

  private getPriorityAction(selectedOptions: any[]): any {
    const actionPriority = [
      SurveyOptionAction.submit,
      SurveyOptionAction.goto,
      SurveyOptionAction.next,
      SurveyOptionAction.previous,
      SurveyOptionAction.skip,
      SurveyOptionAction.none
    ];

    for (const action of actionPriority) {
      const actionOption = selectedOptions.find(option => option.action === action);
      if (actionOption && action !== SurveyOptionAction.none) {
        return actionOption;
      }
    }

    console.log(`[Survey] No valid action found in selected options`);
    return null;
  }

  private async executeAction(actionOption: any) {
    console.log(`[Survey] Handling action '${actionOption.action}' for option '${actionOption._id}'`);

    switch (actionOption.action) {
      case SurveyOptionAction.goto:
        await this.handleGotoAction(actionOption);
        break;
      case SurveyOptionAction.next:
        await this.handleNextAction();
        break;
      case SurveyOptionAction.previous:
        await this.handlePreviousAction();
        break;
      case SurveyOptionAction.submit:
        await this.handleSubmitAction();
        break;
      case SurveyOptionAction.skip:
        await this.handleSkipAction();
        break;
      case SurveyOptionAction.none:
        this.handleNoneAction();
        break;
      default:
        console.warn(`[Survey] Unknown action: ${actionOption.action}`);
        break;
    }
  }

  private async handleGotoAction(actionOption: any) {
    if (actionOption.actionPayload) {
      console.log(`[Survey] Navigating to step: ${actionOption.actionPayload}`);
      await this.navigateToStep(actionOption.actionPayload);
    } else {
      console.warn(`[Survey] Goto action missing actionPayload for option '${actionOption._id}'`);
    }
  }

  private async handleNextAction() {
    console.log(`[Survey] Navigating to next step`);
    await this.navigateNext();
  }

  private async handlePreviousAction() {
    console.log(`[Survey] Navigating to previous step`);
    await this.navigatePrevious();
  }

  private async handleSubmitAction() {
    console.log(`[Survey] Submitting survey`);
    await this.submitSurvey();
  }

  private async handleSkipAction() {
    console.log(`[Survey] Skipping to next step`);
    await this.navigateNext();
  }

  private handleNoneAction() {
    console.log(`[Survey] No action specified for options`);
  }

  // Internal navigation methods that prevent circular calls
  private async navigateToStep(stepId: string) {
    if (!this.isValidStepId(stepId)) {
      return;
    }

    console.log(`[Survey] Navigating to step '${stepId}'`);
    this.performNavigation(() => {
      this.currentStepId = stepId;
    });
  }

  private async navigateNext() {
    if (!this.currentStepId) return;

    const idx = this.stepIdToIndex[this.currentStepId];
    if (idx < this.surveyModel.steps.length - 1) {
      this.performNavigation(() => {
        this.currentStepId = this.surveyModel.steps[idx + 1]._id;
      });
    }
  }

  private async navigatePrevious() {
    if (!this.currentStepId) return;

    const idx = this.stepIdToIndex[this.currentStepId];
    if (idx > 0) {
      this.performNavigation(() => {
        this.currentStepId = this.surveyModel.steps[idx - 1]._id;
      });
    }
  }

  private async submitSurvey() {
    console.log('[Survey] All responses:', JSON.stringify(this.responses, null, 2));
    this.dialogRef.close(Object.values(this.responses));
  }

  private isValidStepId(stepId: string): boolean {
    if (!stepId) {
      console.warn(`[Survey] Cannot navigate to step: stepId is empty`);
      return false;
    }

    if (!Object.prototype.hasOwnProperty.call(this.stepIdToIndex, stepId)) {
      console.warn(`[Survey] Cannot navigate to step '${stepId}': step not found in survey`);
      return false;
    }

    return true;
  }

  private performNavigation(navigationAction: () => void) {
    this.isNavigating = true;
    navigationAction();
    this.loadCurrentStepComponent();
    this.isNavigating = false;
  }

  // Public navigation methods for UI buttons
  async onPrevious() {
    if (!this.currentStepId) return;
    const idx = this.stepIdToIndex[this.currentStepId];
    if (idx > 0) {
      await this.saveCurrentStepResponse();
      this.currentStepId = this.surveyModel.steps[idx - 1]._id;
      this.loadCurrentStepComponent();
    }
  }

  async onNext() {
    if (!this.currentStepId) return;

    console.log(`[Survey] Next button clicked for step: ${this.currentStepId}`);

    // Save the current response first
    await this.saveCurrentStepResponse();

    // Check if we should use conditional navigation
    const currentResponse = this.responses[this.currentStepId];
    if (currentResponse && await this.shouldUseConditionalNavigation(currentResponse)) {
      console.log(`[Survey] Using conditional navigation for step: ${this.currentStepId}`);
      // Let conditional navigation handle it
      await this.handleResponseNavigation(currentResponse);
    } else {
      console.log(`[Survey] Using linear navigation for step: ${this.currentStepId}`);
      // Use linear navigation
      await this.performLinearNextNavigation();
    }
  }

  private async shouldUseConditionalNavigation(response: SurveyResponse): Promise<boolean> {
    if (!this.isValidResponse(response)) {
      return false;
    }

    const selectedOptions = this.getSelectedOptions(response);
    if (selectedOptions.length === 0) {
      return false;
    }

    // Check if any selected option has a non-'none' action
    return selectedOptions.some(option =>
      option.action && option.action !== 'none'
    );
  }

  private async performLinearNextNavigation() {
    if (!this.currentStepId) return;

    const idx = this.stepIdToIndex[this.currentStepId];
    if (idx < this.surveyModel.steps.length - 1) {
      this.currentStepId = this.surveyModel.steps[idx + 1]._id;
      this.loadCurrentStepComponent();
    }
  }

  async gotoStep(stepId: string) {
    if (Object.prototype.hasOwnProperty.call(this.stepIdToIndex, stepId)) {
      await this.saveCurrentStepResponse();
      this.currentStepId = stepId;
      this.loadCurrentStepComponent();
    }
  }

  async onSubmit() {
    await this.saveCurrentStepResponse();
    console.log('[Survey] All responses:', JSON.stringify(this.responses, null, 2));
    this.dialogRef.close(Object.values(this.responses));
    // Dialog close logic handled here
  }

  private async saveCurrentStepResponse() {
    if (!this.canGetResponse()) {
      return;
    }

    console.log(`[Survey] Saving response for step ${this.currentStep?._id}`);
    const response = await this.stepComponentRef!.instance.getResponse();
    console.log(`[Survey] Response: ${JSON.stringify(response)}`);

    if (response) {
      this.onStepResponse(response);
    }
  }

  private canGetResponse(): boolean {
    return !!(this.stepComponentRef &&
             typeof this.stepComponentRef.instance.getResponse === 'function');
  }

  get progressPercent(): number {
    if (!this.currentStepId || !this.surveyModel.steps.length) return 0;
    const idx = this.stepIdToIndex[this.currentStepId];
    return Math.round(((idx + 1) / this.surveyModel.steps.length) * 100);
  }

  // Utility to interpolate mustache variables in a string using all current responses
  private interpolateMustache(str: string | undefined): string {
    if (!str) return '';
    return str.replace(/\{\{(.*?)\}\}/g, (match, varName) => {
      varName = varName.trim();
      let foundValue: string | undefined = undefined;
      const currentLang = this.translate.currentLang === 'fr' ? 'fr' : 'en';
      console.log(`[Survey] Substitution: Looking for variable '{{${varName}}}' in responses...`);
      console.log(`[Survey] Current responses:`, JSON.stringify(this.responses, null, 2));
      for (const [stepId, resp] of Object.entries(this.responses)) {
        if (Array.isArray(resp.response)) {
          for (const answer of resp.response) {
            console.log(`[Survey]   Checking response for stepId='${stepId}', answer._id='${answer._id}', values=`, answer.values);
            if (answer._id === varName && Array.isArray(answer.values) && answer.values.length > 0) {
              const value = answer.values[0];
              if (value !== null && typeof value === 'object' && (value as any).en !== undefined || (value as any).fr !== undefined) {
                foundValue = (value as any)[currentLang] || (value as any)['en'] || '';
              } else if (value != null) {
                foundValue = String(value);
              }
              console.log(`[Survey]   FOUND: '{{${varName}}}' => '${foundValue}'`);
              break;
            }
          }
        }
        if (foundValue !== undefined) break;
      }
      if (foundValue === undefined) {
        console.log(`[Survey]   NOT FOUND: '{{${varName}}}' will be replaced with ''`);
      }
      return foundValue ?? '';
    });
  }

  // Getter for the custom injector to provide stepData and stepResponse
  get dynamicComponentInjector(): Injector | null {
    const step = this.currentStep;
    if (!step) return null;
    const initialResponse = this.responses[step._id];
    // Interpolate mustache variables in both en and fr for title and description
    const interpolateLangString = (mls: any) => {
      if (!mls || typeof mls !== 'object') return mls;
      return {
        en: this.interpolateMustache(mls.en),
        fr: this.interpolateMustache(mls.fr)
      };
    };
    const processedStep = {
      ...step,
      title: interpolateLangString(step.title),
      description: interpolateLangString(step.description),
      options: step.options?.map(option => ({
        ...option,
        text: interpolateLangString(option.text)
      }))
    };
    // Provide stepData, stepResponse, and initialStepResponse as inputs/outputs
    return Injector.create({
      providers: [
        { provide: 'stepData', useValue: processedStep },
        { provide: 'stepResponse', useValue: (response: any) => this.onStepResponse(response) },
        { provide: 'initialStepResponse', useValue: initialResponse },
      ],
      parent: (window as any).ng && (window as any).ng.getInjector ? (window as any).ng.getInjector(this) : undefined
    });
  }

  get stepsLeft(): number {
    if (!this.currentStepId) return 0;
    const idx = this.stepIdToIndex[this.currentStepId];
    return this.surveyModel.steps.length - idx - 1;
  }

  get minutesRemaining(): number {
    if (!this.currentStepId) return 0;
    const idx = this.stepIdToIndex[this.currentStepId];
    let totalSeconds = 0;
    for (let i = idx + 1; i < this.surveyModel.steps.length; i++) {
      const step = this.surveyModel.steps[i];
      const secs = this.stepTypeSeconds[step.stepType] ?? 15;
      totalSeconds += secs;
    }
    return Math.ceil(totalSeconds / 60);
  }

  isCurrentStepValid(): boolean {
    if (!this.currentStep) return false;
    if (this.currentStep.stepType === SurveyStepType.information) return true;
    return this.stepComponentRef?.instance.isValid?.() ?? false;
  }

  get navigableSteps(): SurveyStep[] {
    return this.surveyModel.steps.filter(step =>
      step.stepType !== SurveyStepType.information &&
      step.stepType !== SurveyStepType.introduction &&
      step.stepType !== SurveyStepType.successScreen
    );
  }

  async navigateToStepFromMenu(stepId: string, event: MouseEvent) {
    event.stopPropagation();
    this.isStepsMenuOpen = false;
    await this.saveCurrentStepResponse();
    await this.gotoStep(stepId);
  }

  // Close menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Check if click is outside both the trigger button and menu
    if (this.isStepsMenuOpen &&
        !this.menuTrigger?.nativeElement.contains(event.target) &&
        !this.stepsMenu?.nativeElement.contains(event.target)) {
      this.isStepsMenuOpen = false;
    }
  }

  toggleStepsMenu(event: MouseEvent) {
    event.stopPropagation();
    this.isStepsMenuOpen = !this.isStepsMenuOpen;

    if (this.isStepsMenuOpen) {
      // Position the menu on the left side of the trigger button
      const triggerRect = (event.target as HTMLElement).getBoundingClientRect();
      const dialogRect = this.elementRef.nativeElement.getBoundingClientRect();
      const menuWidth = 200; // minimum width of the menu

      this.menuPosition = {
        top: `${triggerRect.bottom - dialogRect.top}px`,
        left: `${triggerRect.left - dialogRect.left - menuWidth}px`
      };
    }
  }

  getLangValue(mls: { en: string; fr: string }): string {
    const lang = this.translate.currentLang === 'fr' ? 'fr' : 'en';
    return mls?.[lang] || mls?.['en'] || '';
  }

  onClose() {
    this.dialogRef.close();
  }
}
